import removeMarkdown from 'remove-markdown';

import { normalizeSingleLineMarkdownTables } from '~/components/v2/components/workspace/workspace-agent-panel/markdown/normalizeSingleLineMarkdownTables';
import {
  replaceWorkspaceAgentPillMonikersOutsideCode,
  type WorkspaceAgentMonikerCopyLabelSegment,
} from '~/components/v2/components/workspace/workspace-agent-panel/markdown/replaceWorkspaceAgentPillMonikersOutsideCode';
import {
  type GetWorkspaceAgentPillCopyLabelOptions,
  getWorkspaceAgentMonikerCopyLabel,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPillCopyLabel';
import { getFullShareUrl } from '~/utils/shareUrl';

export type GetWorkspaceAgentMessageCopyPlainTextOptions = Pick<
  GetWorkspaceAgentPillCopyLabelOptions,
  'getAssetListMemberClipName' | 'getPillClipName' | 'pills'
> & {
  content: string;
};

const wrapPillCopyLabelInQuotes = (label: string): string => `"${label}"`;

const resolveMonikerCopyLabel = (
  segment: WorkspaceAgentMonikerCopyLabelSegment,
  { getAssetListMemberClipName, getPillClipName, pills }: Omit<GetWorkspaceAgentMessageCopyPlainTextOptions, 'content'>,
): string => {
  if (segment.entityType === 'shareLink') {
    return getFullShareUrl(segment.monikerId);
  }

  // The wire id is the collection uuid, not a public short id — no sync resolver here.
  if (segment.entityType === 'showcase') {
    return wrapPillCopyLabelInQuotes('Showcase');
  }

  if (segment.entityType === 'contentExporter') {
    return 'Download zip';
  }

  return wrapPillCopyLabelInQuotes(
    getWorkspaceAgentMonikerCopyLabel({
      getAssetListMemberClipName,
      getPillClipName,
      monikerId: segment.monikerId,
      pills,
    }),
  );
};

/** Plain text for agent message copy (pill labels + remove-markdown). */
export const getWorkspaceAgentMessageCopyPlainText = (
  options: GetWorkspaceAgentMessageCopyPlainTextOptions,
): string => {
  const trimmed = options.content.trim();

  if (trimmed.length === 0) {
    return '';
  }

  const normalized = normalizeSingleLineMarkdownTables(trimmed);
  const withPillLabels = replaceWorkspaceAgentPillMonikersOutsideCode(normalized, (segment) =>
    resolveMonikerCopyLabel(segment, options),
  );

  return removeMarkdown(withPillLabels)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
